//////////////////
// IMPORTS
//////////////////

// libraries
import * as React from "react";
import ReactTooltip from "react-tooltip";
// import * as ISchema from "@schema";
import { classNames } from "@util/html.util";

// utils
import {
  isSmartHold,
  isFocusTimeSmartHold,
  isClockwiseUnsyncedEvent,
} from "@clockwise/client-commons/src/util/event";
import {
  getCurrentSettings,
  getCategoryLabels,
  getColor,
  EventColorCategory,
} from "@clockwise/client-commons/src/util/event-category-coloring";

// types
import { isSmartHoldNS } from "@clockwise/client-commons/src/util/event";
import {
  IEventColoringSettings,
  IEventCategoryColoring,
} from "@clockwise/client-commons/src/util/event-category-coloring";

// material-ui
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  makeStyles,
  Button,
} from "@material-ui/core";
import { Label, LabelOutlined, KeyboardArrowDown, Palette } from "@material-ui/icons";

// styles
import { greys, text, colors } from "@styles/color.styles";
import { fontFamilySans } from "@styles/type.styles";
import { opacity } from "@util/color.util";

const useStyles = makeStyles({
  labelContainer: {
    display: "flex",
    alignItems: "center",
    fontWeight: 400,
    padding: 0,
    width: "fit-content",
    fontFamily: fontFamilySans,
    marginTop: -8, // Hack - Used with the marginTops of labelText and ingoreTag to add
    // marginTop to the ingoreTag when flex wrapped to a second line.
    marginLeft: -8, // HackII - Used with the marginLefts of labelText, ignoreTag, and confirm button to
    // left align when flex wrapped to a second line.
    flexWrap: "wrap",
  },
  baseLabelIcon: {
    marginLeft: 1,
    marginRight: 8,
  },
  labelIconEmpty: {
    border: `1px solid ${greys.blueGrey}`,
  },
  labelCategoryStyle: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  labelText: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    fontSize: 14,
    color: greys.standard,
    backgroundColor: greys.paleGrey,
    borderRadius: 20,
    padding: `1px 9px`,
    lineHeight: "26px",
    cursor: "pointer",
    marginTop: 8,
    marginLeft: 8,
  },
  labelTextHover: {
    backgroundColor: greys.cloudyBlue,
  },
  labelText__unconfirmed: {
    backgroundColor: greys.white,
    border: `1px dashed ${greys.slate}`,
  },
  labelText__loading: {
    minWidth: 80,
    width: "100%",
    minHeight: 24,
  },
  menuItemIcon: {
    height: 20,
    width: 20,
    left: 10,
    top: 6,
  },
  dropDownIcon: {
    color: greys.slate,
    marginLeft: 4,
    "&.hidden": {
      visibility: "hidden",
    },
  },
  stopIgnoreMenuItem: {
    color: text.error,
    fontSize: 14,
  },
  disabled: {
    cursor: "not-allowed",
  },
  pointerEventsNone: {
    pointerEvents: "none",
  },
  icon: {
    color: greys.blueGrey,
    width: 18,
    height: 18,
    marginLeft: 0,
    marginRight: 12,
    flexShrink: 0,
  },
  transparentTag: {
    backgroundColor: colors.yellow.lightest,
    color: colors.yellow.darker,
    border: `1px solid ${colors.yellow.darker}`,
    borderRadius: 20,
    fontSize: `${13 / 16}rem`,
    fontWeight: 400,
    lineHeight: "22px",
    display: "flex",
    alignItems: "center",
    padding: `1px 7px`,
    marginTop: 8,
    marginRight: 1,
    marginLeft: 8,
  },

  ignoreTag: {
    backgroundColor: colors.yellow.lightest,
    color: colors.yellow.darker,
    border: `1px solid ${colors.yellow.darker}`,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "22px",
    display: "flex",
    alignItems: "center",
    padding: `1px 4px 1px 7px`,
    cursor: "pointer",
    marginTop: 8,
    marginRight: 1,
    marginLeft: 8,
  },
  ignoreTagHovered: {
    backgroundColor: colors.yellow.lighter,
  },
  cancelIcon: {
    height: 14,
    width: 14,
    marginLeft: 4,
  },
  tooltip: {
    fontFamily: fontFamilySans,
  },
  loadingSpan: {
    width: 20,
  },
  confirmActionsWrapper: {
    display: "flex",
    marginTop: 8,
    marginLeft: 2,
  },
  confirm: {
    textTransform: "none",
    borderRadius: 20,
    padding: `2px 6px`,
    "&:hover": {
      backgroundColor: opacity(colors.green.standard, 0.1),
    },
  },
  cancelConfirm: {
    color: colors.red.standard,
    textTransform: "none",
    borderRadius: 20,
    padding: `2px 6px`,
    "&:hover": {
      backgroundColor: opacity(colors.red.standard, 0.1),
    },
  },
  confirmationMessage: {
    fontFamily: fontFamilySans,
    color: colors.peach.darker,
    fontSize: "0.75rem",
    lineHeight: 1.5,
    margin: "4px 0px",
    maxWidth: 350,
    width: "100%",
  },
});

export interface EventCategoryPickerProps {
  event: isSmartHoldNS.IEvent | null;
  eventColoringSettings: IEventColoringSettings<IEventCategoryColoring>;
  selectedCategory: string | null;
  onSelectCategory: (eventCategory: string) => void;
  isTransparentAndNotAllDayEvent: boolean;
  openColorCodingSettings: () => void;
  menuContainer?: HTMLElement;
  loading?: boolean;
}

const toolTipId = `tooltip_for_event_summary_category`;
const ignoreToolTipId = `tooltip_for_ignore_msg`;
const editColorsValue = "EDIT_COLORS";

//////////////////
// COMPONENT
//////////////////
export const EventCategoryPicker = ({
  event,
  eventColoringSettings,
  selectedCategory,
  onSelectCategory,
  isTransparentAndNotAllDayEvent,
  openColorCodingSettings,
  menuContainer,
  loading,
}: EventCategoryPickerProps) => {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [categoryPickerHovered, setCategoryPickerHovered] = React.useState(false);
  const anchorEl = React.useRef<HTMLDivElement | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [currentCategory, setCurrentCategory] = React.useState(selectedCategory);
  const [categoryToSelect, setCategoryToSelect] = React.useState<string | null>(null);

  React.useEffect(() => {
    setCurrentCategory(selectedCategory);
  }, [selectedCategory]);

  const onCancelConfirm = () => {
    setShowConfirm(false);
    setCategoryToSelect("");
  };

  const onConfirm = () => {
    if (!categoryToSelect) {
      return;
    }

    onSelectCategory(categoryToSelect);
    setShowConfirm(false);
    setCurrentCategory(categoryToSelect);
    setCategoryToSelect(null);
  };

  const onClickOpen = (_event: React.MouseEvent<HTMLDivElement>) => {
    // This prevents ghost click.
    _event.preventDefault();

    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onSelect = (eventCategory: string) => {
    if (!eventCategory) {
      return;
    }
    setOpen(false);

    if (eventCategory === editColorsValue) {
      openColorCodingSettings();
    } else if (
      currentCategory === EventColorCategory.Holiday &&
      eventCategory !== EventColorCategory.Holiday
    ) {
      setShowConfirm(true);
      setCategoryToSelect(eventCategory);
    } else {
      onSelectCategory(eventCategory);
    }
  };

  const isFocusTime = event && isFocusTimeSmartHold(event);
  const smartHold = event && isSmartHold(event);
  const clockwiseUnsyncedEvent = event && isClockwiseUnsyncedEvent(event);
  const isSyncedFocusTime = isFocusTime && !clockwiseUnsyncedEvent;
  const menuItems = getCurrentSettings(eventColoringSettings);
  const currentCategoryLabel = currentCategory ? getCategoryLabels(currentCategory).label : "none";
  const currentCategoryColor = currentCategory
    ? getColor(currentCategory, eventColoringSettings, !!isSyncedFocusTime)
    : null;

  const categoryToSelectLabel = categoryToSelect
    ? getCategoryLabels(categoryToSelect).label
    : "none";
  const categoryToSelectColor = categoryToSelect
    ? getColor(categoryToSelect, eventColoringSettings, false)
    : null;

  const isLoading = loading || !event;

  const labelClass = classNames([
    classes.labelText,
    categoryPickerHovered || open ? classes.labelTextHover : undefined,
    showConfirm ? classes.labelText__unconfirmed : undefined,
    isLoading ? classes.labelText__loading : undefined,
  ]);

  const renderLabelIcon = () => {
    // if we're waiting on a confirmation,
    // render the selected event cat color
    if (showConfirm) {
      return (
        <ListItemIcon className={classes.baseLabelIcon}>
          {!!categoryToSelectColor && !isLoading ? ( // When isLoading, show LabelOutlined
            <Label className={classes.menuItemIcon} htmlColor={categoryToSelectColor} />
          ) : (
            <LabelOutlined className={classes.menuItemIcon} />
          )}
        </ListItemIcon>
      );
    }

    return (
      <ListItemIcon className={classes.baseLabelIcon}>
        {!!currentCategoryColor && !isLoading ? ( // When isLoading, show LabelOutlined
          <Label className={classes.menuItemIcon} htmlColor={currentCategoryColor} />
        ) : (
          <LabelOutlined className={classes.menuItemIcon} />
        )}
      </ListItemIcon>
    );
  };

  return (
    <div className={smartHold ? classes.disabled : ""}>
      <div
        className={classNames([
          classes.labelContainer,
          (smartHold || isLoading) && classes.pointerEventsNone,
        ])}
      >
        <div
          className={labelClass}
          onClick={loading ? undefined : onClickOpen}
          onMouseEnter={() => setCategoryPickerHovered(true)}
          onMouseLeave={() => setCategoryPickerHovered(false)}
          data-for={toolTipId}
          data-tip
          ref={anchorEl}
        >
          {renderLabelIcon()}
          {isLoading ? (
            <span className={classes.loadingSpan} />
          ) : (
            // if we're waiting on a confirmation,
            // show the selected event category
            <span className={classes.labelCategoryStyle}>
              {showConfirm ? categoryToSelectLabel : currentCategoryLabel}
            </span>
          )}
          <KeyboardArrowDown
            className={classNames([classes.dropDownIcon, isLoading && "hidden"])}
            htmlColor={greys.slate}
          />
        </div>

        {/* Yellow pill to the right of the current category indicating the event is free */}
        {isTransparentAndNotAllDayEvent && (
          <div className={classNames([classes.transparentTag])} data-for={ignoreToolTipId} data-tip>
            Free
          </div>
        )}
        <ReactTooltip id={ignoreToolTipId} effect="solid">
          This event can be scheduled over and will
          <br />
          not be included in Focus Time calculations.
        </ReactTooltip>

        {/* Confirm and cancel actions to the right of the current category
         * acting as a "speed bump" when selecting away from Holidays
         * to any other event category
         */}
        {showConfirm && !isLoading && (
          <div className={classes.confirmActionsWrapper}>
            <Button color="primary" className={classes.confirm} onClick={onConfirm}>
              Confirm
            </Button>
            <Button className={classes.cancelConfirm} onClick={onCancelConfirm}>
              Cancel
            </Button>
          </div>
        )}
      </div>
      <Menu
        open={open}
        getContentAnchorEl={null}
        anchorEl={anchorEl.current}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        onClose={() => onClose()}
        container={menuContainer}
      >
        {menuItems.map((setting, i) => {
          return (
            <MenuItem
              key={i}
              selected={currentCategory === setting.eventCategory}
              onClick={() => onSelect(setting.eventCategory)}
            >
              <ListItemIcon>
                {!!setting.renderColor ? (
                  <Label className={classes.menuItemIcon} htmlColor={setting.renderColor} />
                ) : (
                  <LabelOutlined className={classes.menuItemIcon} />
                )}
              </ListItemIcon>

              <ListItemText inset>{setting.labels.label}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem onClick={() => onSelect(editColorsValue)}>
          <ListItemIcon>
            <Palette className={classes.menuItemIcon} />
          </ListItemIcon>
          <ListItemText inset>Edit category colors</ListItemText>
        </MenuItem>
      </Menu>
      <ReactTooltip className={classes.tooltip} id={toolTipId} effect="solid">
        {smartHold
          ? "The category for this dynamically created event cannot currently be edited"
          : "Edit event category"}
      </ReactTooltip>

      {/* Confirmation message below the current category selection
       * acting as a "speed bump" when selecting away from Holidays
       * to any other event category
       */}
      {showConfirm && !isLoading && (
        <p className={classes.confirmationMessage}>
          Meeting attendees will no longer be marked as Out Of Office and Clockwise could move
          Autopilot meetings onto this day
        </p>
      )}
    </div>
  );
};
